import { LoaderCircle, Mic, Square, WandSparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import LevelMeter from './LevelMeter';
import type { DictationStatus } from './useDictation';
import { MAX_DICTATION_MS } from './useDictation';
import { TOOL_BUTTON, TOOL_ICON_BUTTON, formatElapsed } from './toolStyles';

/** From here on the timer shows how much is left instead of how much has passed. */
const COUNTDOWN_FROM_MS = MAX_DICTATION_MS - 15_000;

type Props = {
  status: DictationStatus;
  elapsedMs: number;
  disabled: boolean;
  autoImprove: boolean;
  onAutoImproveChange: (next: boolean) => void;
  onToggle: () => void;
  onCancel: () => void;
  getLevel: () => number;
};

/**
 * Mic toggle (aria-pressed while recording) plus, during a recording, the
 * live strip: pulsing dot, elapsed time, level meter, the „danach
 * verbessern“ preference and cancel; while the server converts, a quiet
 * spinner with cancel.
 *
 * The mic button is ONE element through every state, so keyboard focus never
 * falls back to <body> when the state changes. While the microphone is being
 * requested or the recording converts it is `aria-disabled` (still focusable,
 * does nothing) instead of `disabled` (which would drop the focus).
 *
 * On a narrow touch screen the strip wraps: preference and cancel move to a
 * second line (`ml-auto`), so every target can stay 40 px.
 */
export function DictationControls({
  status,
  elapsedMs,
  disabled,
  autoImprove,
  onAutoImproveChange,
  onToggle,
  onCancel,
  getLevel,
}: Props) {
  const recording = status === 'recording';
  const transcribing = status === 'transcribing';
  const waiting = status === 'requesting' || transcribing;
  const remaining = MAX_DICTATION_MS - elapsedMs;

  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-1 sm:gap-1.5', status !== 'idle' && 'flex-1')}>
      <button
        type="button"
        onClick={waiting ? undefined : onToggle}
        disabled={disabled}
        aria-disabled={waiting || undefined}
        aria-pressed={recording}
        aria-label="Diktieren"
        title={recording ? 'Aufnahme beenden und umwandeln' : 'Diktieren – die Aufnahme wird zu Text'}
        className={cn(
          TOOL_BUTTON,
          waiting && 'cursor-wait hover:bg-transparent',
          recording &&
            'w-7 justify-center bg-destructive px-0 text-destructive-foreground hover:bg-destructive/90 hover:text-destructive-foreground sm:w-8',
        )}
      >
        {waiting ? (
          <LoaderCircle className="h-4 w-4 text-brand motion-safe:animate-spin" aria-hidden="true" />
        ) : recording ? (
          <Square className="h-3 w-3 fill-current" aria-hidden="true" />
        ) : (
          <Mic className="h-4 w-4" aria-hidden="true" />
        )}
        {status === 'idle' && <span className="hidden sm:inline">Diktieren</span>}
      </button>

      {transcribing && (
        <>
          <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">wird umgewandelt …</span>
          <button type="button" onClick={onCancel} aria-label="Umwandeln abbrechen" title="Abbrechen (Esc)" className={TOOL_ICON_BUTTON}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </>
      )}

      {recording && (
        <>
          <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
            <span className="absolute inset-0 rounded-full bg-destructive/60 motion-safe:animate-ping" />
            <span className="relative h-2 w-2 rounded-full bg-destructive" />
          </span>
          <span
            data-testid="dictation-timer"
            className={cn(
              'w-9 flex-shrink-0 text-xs font-medium tabular-nums',
              elapsedMs >= COUNTDOWN_FROM_MS ? 'text-destructive-strong' : 'text-foreground',
            )}
          >
            <span className="sr-only">{elapsedMs >= COUNTDOWN_FROM_MS ? 'Noch ' : 'Aufnahmezeit '}</span>
            {elapsedMs >= COUNTDOWN_FROM_MS ? `−${formatElapsed(remaining)}` : formatElapsed(elapsedMs)}
          </span>
          <LevelMeter getLevel={getLevel} className="min-w-[16px] flex-1 [@media(pointer:coarse)]:min-w-[40px]" />
          <button
            type="button"
            onClick={() => onAutoImproveChange(!autoImprove)}
            aria-pressed={autoImprove}
            // WCAG 2.5.3: the name contains the visible label.
            aria-label="Danach verbessern"
            title="Nach dem Diktat direkt verbessern"
            className={cn(
              TOOL_BUTTON,
              'ml-auto w-7 justify-center px-0 sm:w-auto sm:px-1.5',
              autoImprove && 'bg-brand-soft text-brand hover:bg-brand-soft hover:text-brand',
            )}
          >
            <WandSparkles className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden text-xs sm:inline">danach verbessern</span>
          </button>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Aufnahme verwerfen"
            title="Verwerfen (Esc)"
            className={cn(TOOL_ICON_BUTTON, 'w-7 sm:w-8')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}

export default DictationControls;
